import { v4 as uuidv4 } from 'uuid';
import { Sheet, Pouch, Group, POUCH_COLORS, GROUP_COLORS, SheetHistoryEntry, HistoryActionType } from '../types';

/**
 * 初期パウチを作成する
 * @returns 初期パウチオブジェクト
 */
const createDefaultPouch = (): Pouch => {
  return {
    id: uuidv4(),
    value: 0,
    multiplier: 1,
    x: 60, // シート作成時と同じ位置
    y: 90, // シート作成時と同じ位置
    isNegative: false,
    zIndex: 200001, // 初期z-index
    color: POUCH_COLORS[0].id, // デフォルト色
    _animateFromZero: false // 初期表示時はアニメーションしない
  };
};

/**
 * 初回起動時専用のウェルカムパウチ群を作成する
 * @returns ウェルカムパウチ配列
 */
const createWelcomePouches = (): Pouch[] => {
  return [
    {
      id: uuidv4(),
      title: "I'm Pouch",
      value: 100,
      multiplier: 1,
      x: 120,
      y: 100,
      isNegative: false,
      zIndex: 200060,
      color: "orange",
      _animateFromZero: false
    },
    {
      id: uuidv4(),
      title: "Press sheet to add",
      value: 200,
      multiplier: 1,
      x: 250,
      y: 100,
      isNegative: false,
      zIndex: 200060,
      color: "pink",
      _animateFromZero: false
    },
    {
      id: uuidv4(),
      title: "Drag to move",
      value: 400,
      multiplier: 1,
      x: 120,
      y: 260,
      isNegative: false,
      zIndex: 200058,
      color: "yellow-green",
      _animateFromZero: false
    },
    {
      id: uuidv4(),
      title: "Long Press to clone",
      value: 800,
      multiplier: 1,
      x: 250,
      y: 260,
      isNegative: false,
      zIndex: 200060,
      color: "orange",
      _animateFromZero: false
    }
  ];
};

/**
 * 初回起動時専用のウェルカムグループ群を作成する
 * @param pouches ウェルカムパウチ配列
 * @returns ウェルカムグループ配列
 */
const createWelcomeGroups = (pouches: Pouch[]): Group[] => {
  if (pouches.length < 4) return [];
  
  return [
    {
      id: uuidv4(),
      path: "M 186.90234375 29.000019073486328 L 323.28515625 29.000019073486328 Q 333.28515625 29.000019073486328 333.28515625 39.00001907348633 L 333.28515625 355.1289253234863 Q 333.28515625 365.1289253234863 323.28515625 365.1289253234863 L 186.90234375 365.1289253234863 Q 176.90234375 365.1289253234863 176.90234375 355.1289253234863 L 176.90234375 39.00001907348633 Q 176.90234375 29.000019073486328 186.90234375 29.000019073486328 Z",
      pouches: [pouches[1].id, pouches[3].id], // "Press canvas to add"と"Long Press to duplicate"のパウチ
      color: "blue",
      placeholderText: "Group 2",
      title: "can overlap"
    },
    {
      id: uuidv4(),
      path: "M 30.714889526367188 12.91405963897705 L 339.7500762939453 12.91405963897705 Q 349.7500762939453 12.91405963897705 349.7500762939453 22.91405963897705 L 349.7500762939453 186.38280963897705 Q 349.7500762939453 196.38280963897705 339.7500762939453 196.38280963897705 L 30.714889526367188 196.38280963897705 Q 20.714889526367188 196.38280963897705 20.714889526367188 186.38280963897705 L 20.714889526367188 22.91405963897705 Q 20.714889526367188 12.91405963897705 30.714889526367188 12.91405963897705 Z",
      pouches: [pouches[0].id, pouches[1].id], // "I'm Pouch"と"Press canvas to add"のパウチ
      color: "yellow",
      placeholderText: "Group 1",
      title: "I'm Group - Drag sheet to add"
    }
  ];
};

/**
 * 初期履歴エントリを作成する
 * @param pouches パウチ配列
 * @param groups グループ配列
 * @returns 初期履歴エントリ
 */
const createInitialHistoryEntry = (pouches: Pouch[], groups: Group[]): SheetHistoryEntry => {
  return {
    timestamp: new Date(),
    groups: groups,
    pouches: pouches,
    description: "Initial state",
    type: HistoryActionType.INIT,
    targetId: undefined
  };
};

/**
 * デフォルトシートを作成する
 * @param sheetNumber シート番号（タイトル用）
 * @returns 初期パウチを含むデフォルトシート
 */
export const createDefaultSheet = (sheetNumber: number = 1): Sheet => {
  const sheetId = uuidv4();
  const defaultPouch = createDefaultPouch();
  const pouches = [defaultPouch];
  const groups: Group[] = [];
  
  // 初期履歴エントリを作成
  const initialHistoryEntry = createInitialHistoryEntry(pouches, groups);
  
  return {
    id: sheetId,
    title: `Sheet ${sheetNumber}`,
    pouches: pouches,
    groups: groups,
    createdAt: new Date(),
    lastPouchValue: 0,
    lastPouchColor: POUCH_COLORS[0].id,
    lastGroupColor: GROUP_COLORS[0].id,
    history: [initialHistoryEntry],
    historyIndex: 0,
    snapshots: []
  };
};

/**
 * 初回起動時専用のウェルカムシートを作成する
 * @returns ウェルカムパウチとグループを含む初回起動用シート
 */
const createWelcomeSheet = (): Sheet => {
  const sheetId = uuidv4();
  const welcomePouches = createWelcomePouches();
  const welcomeGroups = createWelcomeGroups(welcomePouches);
  
  // 1. 初期状態（1個の初期パウチがある状態）の履歴エントリを作成
  const defaultPouch = createDefaultPouch();
  const initialHistoryEntry = createInitialHistoryEntry([defaultPouch], []);
  
  // 2. ウェルカムコンテンツの履歴エントリを作成
  const welcomeHistoryEntry: SheetHistoryEntry = {
    timestamp: new Date(),
    groups: welcomeGroups,
    pouches: welcomePouches,
    description: "Welcome content",
    type: HistoryActionType.ADD_GROUP, // ウェルカムコンテンツの追加として記録
    targetId: undefined
  };
  
  return {
    id: sheetId,
    title: 'Welcome! - I\'m Sheet',
    pouches: welcomePouches,
    groups: welcomeGroups,
    createdAt: new Date(),
    lastPouchValue: 800, // 最後のパウチの値に合わせる
    lastPouchColor: "orange",
    lastGroupColor: "blue",
    history: [initialHistoryEntry, welcomeHistoryEntry], // 0番目: 初期パウチ状態, 1番目: ウェルカム状態
    historyIndex: 1, // 現在はウェルカムコンテンツを表示
    snapshots: []
  };
};

/**
 * 初回起動用のデフォルトシート配列を作成する
 * @returns 初期シートを含む配列
 */
export const createDefaultSheets = (): Sheet[] => {
  return [createDefaultSheet(1)];
};

/**
 * 初回起動用のウェルカムシート配列を作成する
 * @returns ウェルカムシートを含む配列
 */
const createWelcomeSheets = (): Sheet[] => {
  return [createWelcomeSheet()];
};

/**
 * 初期状態用のシート配列を取得する
 * ストレージからシートを読み込み、存在しない場合はデフォルトシートを返す
 * @returns Promise<Sheet[]> - シート配列
 */
export const getInitialSheets = async (): Promise<Sheet[]> => {
  try {
    // 動的インポートでStorageUtilsを読み込み（循環依存を避けるため）
    const { SheetStorage } = await import('./StorageUtils');
    
    // ストレージからシートを取得
    const storedSheets = await SheetStorage.getAllSheets();
    
    if (storedSheets.length > 0) {
      return storedSheets;
    } else {
      const welcomeSheets = createWelcomeSheets();
      
      // ウェルカムシートをストレージに保存
      for (const sheet of welcomeSheets) {
        await SheetStorage.saveSheet(sheet);
      }
      
      // 最初のシートをアクティブに設定
      if (welcomeSheets.length > 0) {
        await SheetStorage.saveActiveSheetId(welcomeSheets[0].id);
      }
      
      return welcomeSheets;
    }
  } catch (error) {
    console.error('初期シート取得でエラーが発生しました:', error);
    // エラー時はメモリ上のウェルカムシートを返す
    return createWelcomeSheets();
  }
};

/**
 * 新規シート作成用の完全なシートを作成する
 * @param sheetsCount 現在のシート数
 * @returns 初期パウチを含む新規シート
 */
export const createNewSheetWithPouch = (sheetsCount: number): Sheet => {
  return createDefaultSheet(sheetsCount + 1);
};

/**
 * 唯一のシート削除時の初期化用シートを作成する
 * @returns 初期パウチを含む初期化シート
 */
export const createInitializationSheet = (): Sheet => {
  return createDefaultSheet(1);
};

/**
 * シート作成とストレージ保存を統合した関数
 * @param sheetsCount 現在のシート数
 * @returns Promise<Sheet> - 作成・保存されたシート
 */
export const createAndSaveNewSheet = async (sheetsCount: number): Promise<Sheet> => {
  const newSheet = createNewSheetWithPouch(sheetsCount);
  
  try {
    // 動的インポートでStorageUtilsを読み込み
    const { SheetStorage } = await import('./StorageUtils');
    
    // シートをストレージに保存
    await SheetStorage.saveSheet(newSheet);
    await SheetStorage.saveActiveSheetId(newSheet.id);
    
    return newSheet;
  } catch (error) {
    console.error('新規シートの保存でエラーが発生しました:', error);
    throw error;
  }
};

/**
 * 初期化シートの作成とストレージ保存を統合した関数
 * @returns Promise<Sheet> - 作成・保存された初期化シート
 */
export const createAndSaveInitializationSheet = async (): Promise<Sheet> => {
  const initSheet = createInitializationSheet();
  
  try {
    // 動的インポートでStorageUtilsを読み込み
    const { SheetStorage } = await import('./StorageUtils');
    
    // シートをストレージに保存
    await SheetStorage.saveSheet(initSheet);
    await SheetStorage.saveActiveSheetId(initSheet.id);
    
    return initSheet;
  } catch (error) {
    console.error('初期化シートの保存でエラーが発生しました:', error);
    throw error;
  }
};
